<template>
  <div class="admin-shell">
    <v-container>
      <div :class="{ 'flex-column': $vuetify.breakpoint.smAndDown }" class="header-section">
        <v-btn
          :class="{ 'mb-5': $vuetify.breakpoint.smAndDown }"
          class="new-type-btn white--text"
          text
          @click="$router.push({ name: 'ReservationsAdmin' })">
          <v-icon left>mdi-chevron-left</v-icon>
          zu den Reservierungen
        </v-btn>
        <v-btn class="new-type-btn white--text" color="primary" @click="openDialog()" :disabled="!canCreateNew">
          <v-icon left>mdi-plus-circle</v-icon>
          Neuen Typ anlegen
        </v-btn>
      </div>

      <v-dialog v-model="dialog" max-width="600px" persistent>
        <v-card dark class="dialog-card">
          <v-card-title class="dialog-title">
            <span>{{ formTitle }}</span>
          </v-card-title>
          <v-card-text class="dialog-body">
            <v-select
              v-if="!isAdmin && isNewItem"
              v-model="selectedPredefinedType"
              :items="predefinedTypeOptions"
              label="Typ auswählen"
              filled
              dark
              :rules="[(v) => !!v || 'Typ ist erforderlich']"
              item-text="title"
              item-value="value"></v-select>

            <v-text-field v-else v-model="editedItem.name" label="Name (z.B. Hydrojet)" filled dark :disabled="!isAdmin"></v-text-field>

            <v-textarea v-model="editedItem.description" label="Beschreibung für die Kunden" filled dark :disabled="!isAdmin"></v-textarea>
            <v-row>
              <v-col cols="12" sm="4">
                <v-text-field v-model.number="editedItem.timeslotDuration" label="Dauer (Min)" type="number" filled dark></v-text-field>
              </v-col>
            </v-row>

            <h3 class="section-headline mt-4">Öffnungszeiten</h3>

            <p class="mb-1 mt-2 font-weight-bold">Reguläre Zeiten (Mo-Fr)</p>
            <v-row>
              <v-col cols="12" sm="6">
                <v-text-field type="time" v-model="editedItem.openingTimes.regular.start" label="Startzeit" filled dark></v-text-field>
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field type="time" v-model="editedItem.openingTimes.regular.end" label="Endzeit" filled dark></v-text-field>
              </v-col>
            </v-row>

            <p class="mb-1 mt-2 font-weight-bold">Samstag</p>
            <v-row>
              <v-col cols="12" sm="6">
                <v-text-field type="time" v-model="editedItem.openingTimes.saturday.start" label="Startzeit" filled dark></v-text-field>
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field type="time" v-model="editedItem.openingTimes.saturday.end" label="Endzeit" filled dark></v-text-field>
              </v-col>
            </v-row>

            <p class="mb-1 mt-2 font-weight-bold">Sonntag</p>
            <v-row>
              <v-col cols="12" sm="6">
                <v-text-field type="time" v-model="editedItem.openingTimes.sunday.start" label="Startzeit" filled dark></v-text-field>
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field type="time" v-model="editedItem.openingTimes.sunday.end" label="Endzeit" filled dark></v-text-field>
              </v-col>
            </v-row>

            <p class="mb-1 mt-2 font-weight-bold">Feiertage</p>
            <v-row>
              <v-col cols="12" sm="6">
                <v-text-field type="time" v-model="editedItem.openingTimes.holiday.start" label="Startzeit" filled dark></v-text-field>
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field type="time" v-model="editedItem.openingTimes.holiday.end" label="Endzeit" filled dark></v-text-field>
              </v-col>
            </v-row>

            <h3 class="section-headline mt-4">Buchungsregeln</h3>
            <v-alert type="info" v-if="isAdmin"
              >Hier legst du fest, wer buchen darf. Die Prüfung der Abos und Pakete funktioniert nach dem <strong>"Enthält-Prinzip"</strong>. Ein
              Mitglied muss nur <strong>mindestens eines</strong> der von dir angegebenen Kriterien besitzen, um eine Buchungsberechtigung zu
              haben.</v-alert
            >
            <v-text-field
              v-model.number="editedItem.advanceSignupLimit"
              type="number"
              label="Voranmeldung möglich (Tage)"
              filled
              dark
              suffix="Tage"
              :disabled="!isAdmin"></v-text-field>

            <v-switch
              color="primary"
              v-model="editedItem.membersOnly"
              label="Buchung nur für Mitglieder mit aktivem Abo"
              dark
              :disabled="!isAdmin"></v-switch>

            <v-expand-transition v-if="isAdmin">
              <div v-if="editedItem.membersOnly">
                <v-combobox
                  v-model="editedItem.requiredSubscriptionTypes"
                  label="Bestimmte Abos voraussetzen (optional)"
                  chips
                  multiple
                  deletable-chips
                  filled
                  dark
                  hint="Nach Eingabe Enter drücken, um ein Abo hinzuzufügen"
                  persistent-hint
                  :disabled="!isAdmin"></v-combobox>

                <v-combobox
                  v-model="editedItem.requiredAddonPackages"
                  label="Bestimmte Zusatzpakete voraussetzen (optional)"
                  chips
                  multiple
                  deletable-chips
                  filled
                  dark
                  class="mt-6"
                  hint="Nach Eingabe Enter drücken, um ein Paket hinzuzufügen"
                  persistent-hint
                  :disabled="!isAdmin"></v-combobox>
              </div>
            </v-expand-transition>

            <v-switch class="mt-6" color="primary" v-model="editedItem.active" label="Dieser Typ ist aktiv und buchbar" dark></v-switch>
          </v-card-text>
          <v-card-actions class="dialog-actions">
            <v-spacer></v-spacer>
            <v-btn text @click="closeDialog" large>Abbrechen</v-btn>
            <v-btn class="save-btn" color="primary" @click="save" large depressed :loading="isSaving">Speichern</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-data-table :headers="headers" :items="reservationTypes" class="elevation-1 type-table" dark :loading="isLoading">
        <template v-slot:item.name="{ item }">
          <div>{{ item.name }}</div>
          <v-chip v-if="item.isDefective" x-small color="orange" class="mt-1">
            <v-icon left x-small>mdi-alert-circle</v-icon>
            Defekt
          </v-chip>
        </template>

        <template v-slot:item.actions="{ item }">
          <v-btn icon @click="editItem(item)"><v-icon>mdi-pencil-outline</v-icon></v-btn>
          <span v-show="false">
            <v-btn v-if="!item.isDefective" icon @click="openDefectiveDialog(item)" color="orange" title="Als defekt melden">
              <v-icon>mdi-alert-circle-outline</v-icon>
            </v-btn>
            <v-btn v-else icon @click="reactivateItem(item)" color="green" title="Wieder aktivieren">
              <v-icon>mdi-check-circle-outline</v-icon>
            </v-btn>
          </span>

          <v-btn icon @click="deleteItem(item)" color="red" v-if="isAdmin"><v-icon>mdi-delete-outline</v-icon></v-btn>
        </template>
      </v-data-table>
    </v-container>
    <v-dialog v-model="defectiveDialog" max-width="500px" persistent>
      <v-card dark class="dialog-card">
        <v-card-title class="dialog-title">Gerät als defekt melden</v-card-title>
        <v-card-text>
          <p>
            Wähle das Datum und die Uhrzeit, ab wann <strong>{{ itemToReport.name }}</strong> als defekt gilt. Alle Buchungen ab diesem Zeitpunkt
            werden storniert.
          </p>
          <v-row>
            <v-col cols="12" sm="7">
              <v-date-picker v-model="defectiveDate" full-width no-title dark color="primary" :min="today"></v-date-picker>
            </v-col>
            <v-col cols="12" sm="5">
              <v-text-field v-model="defectiveTime" label="Uhrzeit" type="time" filled dark></v-text-field>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions class="dialog-actions">
          <v-spacer></v-spacer>
          <v-btn text @click="closeDefectiveDialog">Abbrechen</v-btn>
          <v-btn class="save-btn" color="orange" @click="reportDefective" :loading="isReportingDefective"> Defekt melden </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import api from "@/api"
import userService from "@/services/userService"

export default {
  data: () => ({
    userService,
    dialog: false,
    deleteDialog: false, // Added for delete confirmation
    isLoading: true,
    isSaving: false,
    headers: [
      { text: "Name", value: "name", class: "header-style" },
      { text: "Dauer", value: "timeslotDuration", align: "center", class: "header-style" },
      { text: "Status", value: "active", align: "center", class: "header-style" },
      { text: "Aktionen", value: "actions", sortable: false, align: "right", class: "header-style" },
    ],
    reservationTypes: [],
    editedIndex: -1,
    editedItem: {},
    defaultItem: {
      name: "",
      description: "",
      timeslotDuration: 30,
      openingTimes: {
        regular: { start: "08:00", end: "20:00" },
        saturday: { start: "08:00", end: "20:00" },
        sunday: { start: "08:00", end: "20:00" },
        holiday: { start: "08:00", end: "20:00" },
      },
      active: true,
      studio: null,
      advanceSignupLimit: 7,
      membersOnly: false,
      requiredSubscriptionTypes: [],
      requiredAddonPackages: [],
    },
    defaultText: {
      Hydrojet:
        "Der Hydrojet ist eine Wasser-Massageliege. Warme Wasserstrahlen massieren dich von unten, lockern deine Muskeln und lösen Verspannungen. Ideal nach dem Training oder zur puren Entspannung. Du bleibst dabei komplett trocken.",
      Pelvix:
        "PelviX ist ein Core-Training-System, das deine Beckenboden-, Hüft-, Gesäss- und Oberschenkelmuskulatur trainiert. Es verbessert deine sportliche Leistung, die allgemeine körperliche Stabilität und kann helfen, Verletzungsrisiken zu verringern.",
      PowerPlate:
        "Die Power Plate ist ein Ganzkörper-Trainingsgerät, das mit Vibrationen arbeitet. Durch die intensiven Muskelkontraktionen werden Kraft, Durchblutung und Beweglichkeit verbessert – in kurzer Zeit und mit hoher Effektivität. Ideal für straffere Muskeln, besseren Stoffwechsel und eine gesteigerte Leistungsfähigkeit.",
    },
    // For non-admin creation flow
    selectedPredefinedType: null,
    defectiveDialog: false,
    isReportingDefective: false,
    itemToReport: {},
    defectiveDate: new Date().toISOString().substr(0, 10),
    defectiveTime: null, // <-- GEÄNDERT: Initialwert auf null setzen
    today: new Date().toISOString().substr(0, 10),
  }),

  computed: {
    formTitle() {
      return this.isNewItem ? "Neuer Reservierungstyp" : "Reservierungstyp bearbeiten"
    },
    isAdmin() {
      return this.userService.hasRight("reservation_admin")
    },
    isNewItem() {
      return this.editedIndex === -1
    },
    // Options for the non-admin select, filtered by what already exists
    predefinedTypeOptions() {
      const existingNames = this.reservationTypes.map((rt) => rt.name.toLowerCase())
      const options = [
        { title: "Hydrojet", value: "Hydrojet" },
        { title: "Pelvix", value: "Pelvix" },
        { title: "Power Plate", value: "Power Plate" },
      ]
      if (this.isAdmin) return options // Admin can always see all options
      return options.filter((opt) => !existingNames.includes(opt.value.toLowerCase()))
    },
    // Controls the "Neuen Typ anlegen" button
    canCreateNew() {
      if (this.isAdmin) {
        return true // Admins can always create
      }
      // Non-admins can only create if there are available predefined types
      return this.predefinedTypeOptions.length > 0
    },
  },

  watch: {
    // When a non-admin selects a type, auto-fill the form
    selectedPredefinedType(newValue) {
      if (!this.isAdmin && this.isNewItem && newValue) {
        this.editedItem.name = newValue
        if (newValue === "Hydrojet") {
          this.editedItem.requiredSubscriptionTypes = ["hydro"]
          this.editedItem.requiredAddonPackages = ["hydro"]

          this.editedItem.description = this.defaultText.Hydrojet
        } else if (newValue === "Pelvix") {
          this.editedItem.requiredSubscriptionTypes = ["pelvi"]
          this.editedItem.requiredAddonPackages = ["pelvi"]

          this.editedItem.description = this.defaultText.Pelvix
        } else if (newValue === "Power Plate") {
          this.editedItem.requiredSubscriptionTypes = ["power"]
          this.editedItem.requiredAddonPackages = ["power"]

          this.editedItem.description = this.defaultText.PowerPlate
        }

        if (newValue === "Hydrojet" || newValue === "Pelvix" || newValue === "Power Plate") {
          this.editedItem.timeslotDuration = 30
          this.editedItem.advanceSignupLimit = 3
          this.editedItem.membersOnly = true
        }
      }
    },
  },

  created() {
    this.defectiveTime = this.$moment().format("HH:mm")
    const studioId = this.userService.getStudio()?._id || null
    this.defaultItem.studio = studioId
    this.editedItem = { ...this.defaultItem }
    this.fetchReservationTypes()
  },

  methods: {
    openDefectiveDialog(item) {
      this.itemToReport = item
      this.defectiveDate = new Date().toISOString().substr(0, 10)
      this.defectiveDialog = true
    },

    closeDefectiveDialog() {
      this.defectiveDialog = false
      this.isReportingDefective = false
      this.itemToReport = {}
    },

    async reportDefective() {
      this.isReportingDefective = true
      try {
        // Datum und Zeit zu einem ISO-String kombinieren
        const defectiveDateTime = this.$moment(`${this.defectiveDate} ${this.defectiveTime}`).toISOString()

        await api.reservationTool.reportDefective(this.itemToReport._id, { defectiveFrom: defectiveDateTime })
        this.$toast.success(`${this.itemToReport.name} wurde als defekt gemeldet.`)
        this.fetchReservationTypes()
      } catch (error) {
        console.error("Fehler beim Melden des Defekts:", error)
        this.$toast.error("Fehler: Gerät konnte nicht als defekt gemeldet werden.")
      } finally {
        this.isReportingDefective = false
        this.closeDefectiveDialog()
      }
    },

    // NEUE METHODE ZUR REAKTIVIERUNG
    async reactivateItem(item) {
      if (confirm(`Möchtest du ${item.name} wirklich wieder aktivieren? Betroffene Kunden werden benachrichtigt.`)) {
        try {
          await api.reservationTool.reactivateReservationType(item._id)
          this.$toast.success(`${item.name} wurde wieder aktiviert.`)
          this.fetchReservationTypes() // Liste aktualisieren
        } catch (error) {
          console.error("Fehler bei der Reaktivierung:", error)
          this.$toast.error("Das Gerät konnte nicht reaktiviert werden.")
        }
      }
    },
    async fetchReservationTypes() {
      this.isLoading = true
      try {
        console.log("Lade Reservierungstypen für Admin...")
        const response = await api.reservationTool.getReservationTypesForAdmin()
        this.reservationTypes = response.data
      } catch (error) {
        console.error("Fehler beim Laden der Admin-Reservierungstypen:", error)
      } finally {
        this.isLoading = false
      }
    },

    openDialog() {
      this.editedIndex = -1
      this.editedItem = JSON.parse(JSON.stringify(this.defaultItem))
      this.selectedPredefinedType = null // Reset selection

      this.dialog = true
    },

    editItem(item) {
      this.editedIndex = this.reservationTypes.indexOf(item)
      // Create a full copy and ensure nested objects are handled correctly
      this.editedItem = {
        ...this.defaultItem,
        ...item,
        openingTimes: {
          // Ensure the openingTimes object and its properties exist
          ...this.defaultItem.openingTimes,
          ...(item.openingTimes || {}),
        },
        requiredSubscriptionTypes: [...(item.requiredSubscriptionTypes || [])],
        requiredAddonPackages: [...(item.requiredAddonPackages || [])],
      }
      this.dialog = true
    },

    // Placeholder for a confirmation dialog before actually deleting
    deleteItem(item) {
      // You should implement a confirmation dialog here
      // For example: if (confirm('Are you sure you want to delete this item?')) { ... }
      console.log("Deletion requested for", item)
      // this.deleteItemConfirmed(item); // Call the actual delete method after confirmation
    },

    async deleteItemConfirmed(item) {
      // Actual deletion logic
    },

    closeDialog() {
      this.dialog = false
      this.isSaving = false
      this.selectedPredefinedType = null
      // Use nextTick to avoid seeing the old data flicker before the dialog closes
      this.$nextTick(() => {
        this.editedItem = { ...this.defaultItem }
        this.editedIndex = -1
      })
    },

    async save() {
      this.isSaving = true

      // For a new item by non-admin, ensure name is set from the select
      if (this.isNewItem && !this.isAdmin) {
        if (!this.selectedPredefinedType) {
          // Optional: show an error to the user
          console.error("No type selected")
          this.isSaving = false
          return
        }
        this.editedItem.name = this.selectedPredefinedType
      }

      // If 'membersOnly' is turned off, clear the dependent fields for data consistency
      if (!this.editedItem.membersOnly) {
        this.editedItem.requiredSubscriptionTypes = []
        this.editedItem.requiredAddonPackages = []
      }

      try {
        if (this.editedIndex > -1) {
          await api.reservationTool.updateReservationType(this.editedItem._id, this.editedItem)
        } else {
          await api.reservationTool.createReservationType(this.editedItem)
        }
        this.fetchReservationTypes() // Refresh the data table
      } catch (error) {
        console.error("Error saving reservation type:", error)
        // Optional: Show an error message to the user
      } finally {
        this.isSaving = false
        this.closeDialog()
      }
    },
  },
}
</script>

<style scoped>
/* --- THEME & TikTok-Style --- */
.admin-shell {
  --bg: #121212;
  --card-bg: #1e1e1e;
  --text-primary: #ffffff;
  --text-secondary: #b0b0b0;
  --border: #2c2c2c;
  --primary-text: #000000;

  min-height: 100vh;
  padding: 40px 0;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
}

.header-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
}

.headline {
  font-size: clamp(1.8rem, 5vw, 2.2rem);
  font-weight: 800;
  color: var(--text-primary);
}

.new-type-btn {
  font-weight: 700;
  border-radius: 12px;
}

/* --- DIALOG STYLING --- */
.dialog-card {
  border-radius: 20px !important;
  border: 1px solid var(--border);
}
.dialog-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--text-primary);
}
.dialog-body {
  padding-bottom: 0 !important;
}
.dialog-actions {
  padding: 16px 24px !important;
}
.save-btn {
  border-radius: 12px !important;
  font-weight: 700;
}
:deep(.v-text-field--filled .v-input__control .v-input__slot) {
  background: rgba(0, 0, 0, 0.3) !important;
}
:deep(.v-label) {
  color: var(--text-secondary) !important;
}
.section-headline {
  font-size: 1rem;
  font-weight: 700;
  text-transform: uppercase;
  color: var(--text-secondary);
  border-bottom: 1px solid var(--border);
  padding-bottom: 8px;
  margin-bottom: 16px;
}

/* --- DATA TABLE STYLING --- */
.type-table {
  background-color: var(--card-bg) !important;
  border-radius: 16px !important;
  border: 1px solid var(--border);
}

:deep(th) {
  color: var(--text-secondary) !important;
  font-size: 0.9rem !important;
  text-transform: uppercase;
  font-weight: 600 !important;
}

:deep(tbody tr:hover) {
  background-color: #2a2a2a !important;
}

:deep(.v-data-footer) {
  border-top: 1px solid var(--border);
}
:deep(.v-data-footer__select .v-select__selections) {
  color: var(--text-secondary);
}

.status-chip {
  font-weight: 600;
  text-transform: uppercase;
  font-size: 0.75rem;
  padding: 4px 8px;
  height: auto;
}
</style>
